// "204 traders online" badge with a fixed 5-avatar proof stack — DEV parity
// (P0-57). Avatar IDs are deterministic synthetic strings ("trader-1" ..
// "trader-N") so each session sees the same stack ordering, which keeps
// E2E snapshots stable. The hook subscribes to `/api/v2/traders-online`
// for the live count.

import { useTradersOnline } from "@/features/dashboard/hooks";
import {
  buildUniqueSocialProofAvatarDeck,
  type SocialProofAvatar,
} from "../social-proof-avatar-deck";
import { SocialProofAvatarImage } from "./SocialProofAvatarImage";

const TRADERS_ONLINE_AVATARS = buildUniqueSocialProofAvatarDeck({
  seed: 204,
  count: 5,
});

export function TradersOnlineBadge(): React.JSX.Element {
  const { data } = useTradersOnline();
  const count = data?.count ?? 0;
  return (
    <div
      data-testid="traders-online-badge"
      className="flex items-center gap-3 rounded-2xl border border-border bg-background/80 px-4 py-3"
    >
      <AvatarStack />
      <div className="flex flex-col">
        <span
          data-testid="traders-online-badge-count"
          className="text-sm font-bold text-success"
        >
          {count}
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/55">
          traders online
        </span>
      </div>
    </div>
  );
}

function AvatarStack(): React.JSX.Element {
  return (
    <span
      className="flex w-[136px] items-center gap-1"
      data-testid="traders-online-avatar-stack"
    >
      {TRADERS_ONLINE_AVATARS.map((avatar) => (
        <AvatarStackItem key={avatar.id} avatar={avatar} />
      ))}
    </span>
  );
}

function AvatarStackItem(props: {
  readonly avatar: SocialProofAvatar;
}): React.JSX.Element {
  return (
    <span
      className="relative inline-flex size-6 overflow-hidden rounded-full"
      data-testid="traders-online-avatar-frame"
    >
      <SocialProofAvatarImage
        avatar={props.avatar}
        size={24}
        alt=""
        loading="eager"
      />
    </span>
  );
}
